import {FormattedMessage, IntlShape} from "react-intl";
import {MTColumnsType} from "@utils/pagination";
import {Localization, Options} from "@material-table/core";
import React from "react";
import {HomeLink} from "@feature/home/types";
import Badge from "@ui-components/Badge";
import {getMenuPermissions} from "@feature/home/lib/utils";
import RadioGroup from "@ui-components/RadioGroup";
import menu from "@ui-components/AppNavigation/menu";
import Input from "@ui-components/Input";
import SearchSelect from "@ui-components/SearchSelect";
import {ExclamationCircleIcon, InformationCircleIcon} from "@heroicons/react/24/outline";

export const getColumns = (
  intl: IntlShape,
  countryCode: string,
  roles: string[] = [],
) => [
  {
    title: intl.formatMessage({id: "order", defaultMessage: "Ordine"}),
    field: "order",
    type: "numeric",
    defaultSort: "asc",
    width: "1%",
    editable: "onUpdate",
  },
  {
    title: intl.formatMessage({id: "title_description", defaultMessage: "Titolo/Descrizione"}),
    field: "locale_key",
    type: "string",
    render: ({locale_key}) => {
      const getLocaleValue = (key: string, defaultValue?: string, ...args: any[]) => {
        const found = key in intl.messages;
        return !found && defaultValue !== undefined ? defaultValue : intl.formatMessage({id: key}, ...args);
      }
      const title = getLocaleValue(locale_key, "");
      const description = getLocaleValue(`${locale_key}_description`, "");
      return (
        <div className="flex flex-col">
          <p className={!!title ? "font-medium" : "italic"}>
            {title || locale_key}
          </p>
          <p className="text-xs">
            {description || <>&ndash;</>}
          </p>
        </div>
      )
    },
    editComponent: ({value, onChange}) => {
      const getLocaleValue = (key: string, defaultValue?: string, ...args: any[]) => {
        const found = key in intl.messages;
        return !found && defaultValue !== undefined ? defaultValue : intl.formatMessage({id: key}, ...args);
      }
      return (
        <div className="flex flex-col">
          {/* @ts-ignore */}
          <Input
            type="text"
            value={value || ""}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              onChange(e.target.value);
            }}
            placeholder={intl.formatMessage({id: "enter_locale_key", defaultMessage: "Chiave di traduzione"})}
          />
          <div className="flex flex-col gap-1 text-xs">
            {[
              {label: "title", key_extension: ""},
              {label: "description", key_extension: "_description"}
            ].map(({label, key_extension}) => {
              const key = `${value}${key_extension}`;
              const translation = getLocaleValue(key, "");
              const found = translation !== "";
              return (
                <div key={label} className="flex flex-col">
                  <p className="font-semibold">
                    <FormattedMessage id={label} />
                  </p>
                  {found && <p>{translation}</p>}
                  {!found && (
                    <p className="flex flew-row gap-1 items-center text-yellow-600">
                      <ExclamationCircleIcon className="w-3" />
                      <FormattedMessage id="translation_not_found" defaultMessage="Traduzione non trovata"/>
                    </p>
                  )}
                  {!!value && <p className="text-xxs">({key})</p>}
                </div>
              )
            })}
          </div>
        </div>
      )
    }
  },
  {
    title: intl.formatMessage({id: "link", defaultMessage: "Link"}),
    field: "internal_section_key",
    render: ({internal_section_key, external_link_url}: HomeLink) => {
      const isInternal = !!internal_section_key;
      return (
        <div className="flex flex-col gap-0.5">
          <div>
            <Badge
              type={isInternal ? "success" : "info"}
              text={intl.formatMessage({
                id: isInternal ? "internal_ms" : "external_ms",
                defaultMessage: isInternal ? "Interno" : "Esterno"
              })}
              textSize="text-xs"
              onClick={undefined}
            />
          </div>
          <div className="flex flex-col text-xs">
            {
              isInternal
                ? <p>
                  <FormattedMessage id="section" defaultMessage="Sezione"/>:{" "}
                  <span className="font-medium">
                    <FormattedMessage id={internal_section_key} />
                  </span>
                </p>
                : (
                  <a
                    rel="noreferrer"
                    target="_blank"
                    href={external_link_url || "#"}
                    className="overflow-ellipsis text-black whitespace-nowrap font-medium"
                  >
                    {external_link_url}
                  </a>
                )
            }
          </div>
        </div>
      )
    },
    editComponent: ({rowData, onRowDataChange}) => {
      const isInternal = !!rowData.internal_section_key;
      return (
        <div className="flex flex-col gap-1">
          <RadioGroup
            id={`rg-home-link-type-${rowData.id ?? "new"}`}
            title={undefined}
            description={undefined}
            options={[
              {
                name: intl.formatMessage({id: "internal_ms", defaultMessage: "Interno"}),
                value: "internal",
              },
              {
                name: intl.formatMessage({id: "external_ms", defaultMessage: "Esterno"}),
                value: "external",
              },
            ]}
            onChange={(selected: "internal" | "external") => {
              const overrides = selected === "internal"
                ? { external_link_url: undefined, internal_section_key: "worksites" }
                : { external_link_url: "https://www.example.com", internal_section_key: undefined };
              onRowDataChange({...rowData, ...overrides});
            }}
            currentValue={isInternal ? "internal" : "external"}
            horizontal
          />
          {isInternal && (
            // @ts-ignore
            <SearchSelect
              options={menu.reduce(
                (acc, curr) => [
                  ...acc,
                  !!curr.dropdown
                    ? {
                      label: intl.formatMessage({id: curr.name}),
                      options: curr.dropdown.map(dd => ({
                        value: dd.name,
                        label: intl.formatMessage({id: dd.name}),
                      })),
                    }
                    : {value: curr.name, label: intl.formatMessage({id: curr.name})},
                ],
                [] as { value?: string; label: string; options?: { value: string; label: string; }[]; }[])}
              value={!!rowData.internal_section_key ? {
                value: rowData.internal_section_key,
                label: intl.formatMessage({id: rowData.internal_section_key})
              } : null}
              onChange={({value}: {value: string, label: string}) => {
                onRowDataChange({...rowData, internal_section_key: value});
              }}
              label={intl.formatMessage({id: "select_section", defaultMessage: "Seleziona una sezione"})}
            />
          )}
          {!isInternal && (
            // @ts-ignore
            <Input
              type="text"
              value={rowData.external_link_url || ""}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                onRowDataChange({...rowData, external_link_url: e.target.value});
              }}
              placeholder={intl.formatMessage({id: "enter_url", defaultMessage: "Inserisci l'URL"})}
            />
          )}
        </div>
      )
    },
  },
  {
    title: intl.formatMessage({id: "role_visibility", defaultMessage: "Visibilità per ruolo"}),
    field: "external_link_enabled_roles",
    render: ({internal_section_key, external_link_enabled_roles}) => {
      const isInternal = !!internal_section_key;
      const roles = isInternal
        ? getMenuPermissions(countryCode)[internal_section_key] || "*"
        : external_link_enabled_roles || "*";
      if (Array.isArray(roles))
        return roles.length === 0
          ? <span className="italic">
            <FormattedMessage id="none_ms" defaultMessage="Nessuno"/>
          </span>
          : (
            <ul className="list-disc list-inside text-xs">
              {roles.map((role, i) => (
                <li key={i}>
                  <FormattedMessage id={`user_type_${role}`} defaultMessage={role} />
                </li>
              ))}
            </ul>
          )
      else if (roles === "*")
        return <span className="italic">
          <FormattedMessage id="all_ms" defaultMessage="Tutti"/>
        </span>;
      else
        throw new Error(`Invalid roles format: ${roles}`);
    },
    editComponent: ({rowData, onRowDataChange}) => {
      const isInternal = !!rowData.internal_section_key;
      if (isInternal) {
        const roles = getMenuPermissions(countryCode)[rowData.internal_section_key!] || "*";
        let content;
        if (Array.isArray(roles)) {
          content = roles.length === 0
            ? <span className="italic">
              {intl.formatMessage({id: "none_ms", defaultMessage: "Nessuno"})}
            </span>
            : (
              <ul className="list-disc list-inside text-xs">
                {roles.map((role, i) => (
                  <li key={i}>
                    <FormattedMessage id={`user_type_${role}`} defaultMessage={role} />
                  </li>
                ))}
              </ul>
            );
        } else if (roles === "*") {
          content = <span className="italic">
            {intl.formatMessage({id: "all_ms", defaultMessage: "Tutti"})}
          </span>;
        }
        else {
          throw new Error(`Invalid roles format: ${roles}`);
        }
        return (
          <div className="flex flex-col gap-2">
            {content}
            <div className="flex flex-row gap-2 items-start text-blue-600 text-xs">
              <InformationCircleIcon className="w-4 flex-shrink-0" />
              <FormattedMessage
                id="internal_link_roles_info"
                defaultMessage="La visibilità per ruolo dei link interni NON è modificabile, ma viene ereditata dalle autorizzazioni di accesso in essere per la sezione selezionata."
              />
            </div>
          </div>
        );
      } else {  // is external
        return (
          <div className="flex flex-col gap-1">
            <RadioGroup
              id={`rg-home-link-roles-${rowData.id ?? "new"}`}
              title={undefined}
              description={undefined}
              options={[
                {
                  name: intl.formatMessage({id: "all_mp", defaultMessage: "Tutti"}),
                  value: "all",
                },
                {
                  name: intl.formatMessage({id: "specific_roles", defaultMessage: "Ruoli specifici"}),
                  value: "specific",
                },
                {
                  name: intl.formatMessage({id: "none_ms", defaultMessage: "Nessuno"}),
                  value: "none",
                },
              ]}
              onChange={(selected: "all" | "specific") => {
                const overrides = selected === "all"
                  ? { external_link_enabled_roles: undefined }
                  : selected === "specific"
                    ? { external_link_enabled_roles: ["admin"] }
                    : { external_link_enabled_roles: [] };
                onRowDataChange({...rowData, ...overrides});
              }}
              currentValue={
                !rowData.external_link_enabled_roles
                  ? "all"
                  : Array.isArray(rowData.external_link_enabled_roles) && rowData.external_link_enabled_roles.length > 0
                    ? "specific"
                    : "none"
              }
              horizontal
            />
            {Array.isArray(rowData.external_link_enabled_roles) && rowData.external_link_enabled_roles.length > 0 && (
              // @ts-ignore
              <SearchSelect
                isMulti
                options={roles.map((role) => ({
                  value: role,
                  label: intl.formatMessage({id: `user_type_${role}`, defaultMessage: role}),
                }))}
                value={rowData.external_link_enabled_roles?.map((role: string) => ({
                  value: role,
                  label: intl.formatMessage({id: `user_type_${role}`, defaultMessage: role})
                })) || []}
                onChange={(o: { value: string; label: string }[]) => {
                  const selectedRoles = o.map((item) => item.value);
                  onRowDataChange({...rowData, external_link_enabled_roles: selectedRoles});
                }}
              />
            )}
          </div>
        )
      }
    }
  },
] as MTColumnsType<HomeLink>;


export const OPTIONS = {
  search: false,
  filtering: false,
  toolbar: false,
  sorting: false,
  paging: true,
  pageSize: 10,
  pageSizeOptions: [5, 10, 20],
  showTitle: false,
  headerStyle: {fontWeight: "bold", fontSize: 15, paddingTop: 5, paddingBottom: 5},
  addRowPosition: "first",
} as Options<HomeLink>;

export const LOCALIZATION = {
  header: {
    actions: "",
  },
  body: {
    emptyDataSourceMessage: "Nessun risultato disponibile",
  },
  pagination: {
    labelDisplayedRows: "{from}-{to} di {count} righe",
    labelRowsSelect: "righe",
  }
} as Localization;